import SEOHead from "@/components/SEOHead";
import comparisonPost from "@/content/lumient-comparison.json";

const posts = [
  {
    title: comparisonPost.title,
    description:
      "A practical guide to where extraction, retrieval, automation, data platforms, and operational records fit in evidence-heavy operations.",
    date: "April 29, 2026",
    dateTime: comparisonPost.datePublished,
    readingTime: "8 min read",
    href: `/compare/${comparisonPost.slug}`,
    type: "Featured Field Note",
    topics: ["Document AI", "RAG", "Workflow Automation", "Enterprise Agents", "Operational Records"],
  },
];

const featuredPost = posts[0];

const topicFrames = [
  {
    title: "Operational records",
    body: "How document-heavy work becomes state that stays useful after the first run.",
  },
  {
    title: "Agent infrastructure",
    body: "Why agents need records, proof, policies, and review gates before they can act.",
  },
  {
    title: "Workflow design",
    body: "How recurring checks become reusable systems instead of manual loops.",
  },
];

const Blog = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <SEOHead
      title="Blog — Lumient"
      description="Field notes on operational state, evidence, workflows, and enterprise agents."
      canonical="https://lumient.ai/blog"
    />

    <main className="flex-1 w-full">
      <section className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-16 pt-28 pb-16 lg:pt-36 lg:pb-20">
          <p className="text-xs font-medium tracking-[0.2em] uppercase mb-5" style={{ color: "oklch(var(--primary))" }}>
            RESOURCES
          </p>
          <h1 className="font-serif font-semibold text-[clamp(3rem,5vw,4.75rem)] leading-[1.02] tracking-[-0.025em] text-[#0B0B0B] mb-6">
            Blog
          </h1>
          <p className="max-w-3xl text-[1.1rem] font-light leading-[1.65] text-muted-foreground">
            Field notes on operational state, evidence, workflows, and enterprise agents.
          </p>
        </div>
      </section>

      <section className="bg-surface-subtle py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <article
            aria-label={`Featured field note: ${featuredPost.title}`}
            className="rounded-[1.5rem] border border-border bg-surface-panel px-6 py-7 transition-colors duration-300 hover:border-[oklch(var(--primary)_/_0.32)] lg:px-10 lg:py-9"
          >
            <div className="max-w-5xl">
              <p className="text-xs font-medium tracking-[0.2em] uppercase mb-5" style={{ color: "oklch(var(--primary))" }}>
                {featuredPost.type.toUpperCase()}
              </p>
              <h2 className="font-serif font-semibold text-[clamp(1.9rem,2.8vw,2.85rem)] leading-[1.07] tracking-[-0.022em] text-[#0B0B0B] mb-5">
                {featuredPost.title}
              </h2>
              <p className="max-w-3xl text-base lg:text-[1.05rem] font-light leading-[1.7] text-muted-foreground mb-6">
                {featuredPost.description}
              </p>
              <div className="mb-7 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-muted-foreground">
                {featuredPost.topics.map((topic, index) => (
                  <span key={topic} className="inline-flex items-center gap-x-2">
                    {index > 0 ? (
                      <span aria-hidden="true" className="text-border">
                        ·
                      </span>
                    ) : null}
                    <span>{topic}</span>
                  </span>
                ))}
              </div>
              <div className="flex flex-col items-start gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
                <time dateTime={featuredPost.dateTime} className="text-xs font-medium text-muted-foreground">
                  {featuredPost.date} · {featuredPost.readingTime}
                </time>
                <a
                  href={featuredPost.href}
                  className="text-sm font-semibold text-primary underline-offset-4 transition-colors hover:text-[#8A5B00] hover:underline"
                >
                  Read the comparison →
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="bg-background border-t border-border py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-12 gap-8 lg:gap-12">
            <div className="col-span-12 lg:col-span-4">
              <h2 className="font-serif font-semibold text-[2rem] leading-[1.1] tracking-[-0.02em] text-[#0B0B0B] mb-5">
                What we write about
              </h2>
              <p className="text-sm leading-[1.75] text-muted-foreground">
                Operational records, evidence-heavy workflows, and agent infrastructure for accountable AI operations.
              </p>
            </div>

            <div className="col-span-12 grid gap-8 sm:grid-cols-3 lg:col-span-8">
              {topicFrames.map((topic) => (
                <section key={topic.title} className="border-t border-border pt-5">
                  <h3 className="font-serif font-semibold text-[1.25rem] leading-[1.2] tracking-[-0.015em] text-[#0B0B0B] mb-3">
                    {topic.title}
                  </h3>
                  <p className="text-sm leading-[1.7] text-muted-foreground">{topic.body}</p>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export default Blog;
